import { Box, TextField, Typography } from '@mui/material';
import React from 'react';

interface ProfileFormFieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  displayValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  renderDisplay?: () => React.ReactNode;
  dataTestId?: string;
  helperText?: string;
}

export const ProfileFormField: React.FC<ProfileFormFieldProps> = ({
  label,
  value,
  isEditing,
  displayValue,
  onChange,
  type = 'text',
  placeholder,
  icon,
  renderDisplay,
  dataTestId,
  helperText,
}) => {
  return (
    <Box>
      {isEditing
        ? (
            <Box>
              {/* Label above the input when editing */}
              <Typography
                variant="body2"
                sx={{
                  mb: 1.5,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                {label}
              </Typography>
              <TextField
                fullWidth
                value={value}
                onChange={onChange}
                type={type}
                variant="outlined"
                placeholder={placeholder || `Enter ${label.toLowerCase()}`}
                helperText={helperText}
                inputProps={{
                  'data-testid': dataTestId,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    'borderRadius': 2.5,
                    'backgroundColor': 'background.paper',
                    'fontSize': '0.9375rem',
                    'transition': 'all 0.2s ease',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0b74da',
                    },
                    '&.Mui-focused': {
                      'boxShadow': '0 0 0 3px rgba(11, 116, 218, 0.14)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                        borderColor: '#0b74da',
                      },
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '14px 16px',
                  },
                }}
              />
            </Box>
          )
        : (
            <Box>
              {icon && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  {icon}
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {label}
                  </Typography>
                </Box>
              )}
              {!icon && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              )}
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderRadius: 2.5,
                  backgroundColor: 'rgba(120, 187, 241, 0.06)',
                  border: '1px solid',
                  borderColor: 'rgba(120, 187, 241, 0.22)',
                  transition: 'all 0.2s ease',
                }}
              >
                {renderDisplay
                  ? (
                      renderDisplay()
                    )
                  : (
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: (displayValue || value) ? 400 : 500,
                          fontSize: '0.9375rem',
                          color: (displayValue || value) ? 'text.primary' : 'text.secondary',
                          fontStyle: (displayValue || value) ? 'normal' : 'italic',
                        }}
                      >
                        {displayValue || value || 'Not set'}
                      </Typography>
                    )}
              </Box>
            </Box>
          )}
    </Box>
  );
};
